/**
 * Route Registry
 * Centralized registry for route matching, navigation generation, and permission checks
 *
 * The registry provides:
 * - Route lookup by path (with dynamic segment support)
 * - Navigation generation based on user permissions
 * - Route access validation
 * - Parameter extraction from paths
 */

import {
  routeConfig,
  NAV_GROUP_LABELS,
  NAV_GROUP_ORDER,
  AUTH_REDIRECTS,
} from './config';
import type {
  RouteConfig,
  AuthContext,
  NavItem,
  NavGroup,
  NavPosition,
  GeneratedNavigation,
  AppUserRole,
} from './types';
import { STAFF_ROLE_SLUGS, ADMIN_ROLE_SLUGS, legacyTierForSlug } from '@/lib/auth/role-slugs';

// ============================================================================
// Route Registry Class
// ============================================================================

class RouteRegistry {
  private routes: Map<string, RouteConfig> = new Map();
  private byPath: Map<string, RouteConfig> = new Map();
  private sortedPatterns: string[] = [];

  constructor() {
    this.initialize();
  }

  /**
   * Initialize registry with all routes
   */
  private initialize(): void {
    // Register all routes
    this.registerRoutes(routeConfig);

    // Sort patterns by specificity (more specific first)
    this.sortPatterns();
  }

  /**
   * Register routes recursively (including children)
   */
  private registerRoutes(routes: RouteConfig[]): void {
    for (const route of routes) {
      this.routes.set(route.id, route);
      this.byPath.set(route.path, route);

      if (route.children) {
        this.registerRoutes(route.children);
      }
    }
  }

  /**
   * Sort path patterns by specificity for matching
   * More segments = more specific
   * Fewer wildcards = more specific
   */
  private sortPatterns(): void {
    const patterns = Array.from(this.byPath.keys());

    this.sortedPatterns = patterns.sort((a, b) => {
      const aSegments = a.split('/').filter(Boolean);
      const bSegments = b.split('/').filter(Boolean);

      // More segments = higher priority
      if (bSegments.length !== aSegments.length) {
        return bSegments.length - aSegments.length;
      }

      // Fewer wildcards = higher priority
      const aWildcards = aSegments.filter((s) => s.startsWith('[')).length;
      const bWildcards = bSegments.filter((s) => s.startsWith('[')).length;
      return aWildcards - bWildcards;
    });
  }

  // ==========================================================================
  // Route Lookup
  // ==========================================================================

  /**
   * Get route by ID
   */
  getById(id: string): RouteConfig | undefined {
    return this.routes.get(id);
  }

  /**
   * Get route by path (supports dynamic segments)
   */
  getByPath(pathname: string): RouteConfig | undefined {
    // Normalize path (remove trailing slash)
    const normalizedPath = pathname === '/' ? '/' : pathname.replace(/\/$/, '');

    // Try exact match first
    const exact = this.byPath.get(normalizedPath);
    if (exact) return exact;

    // Try pattern matching for dynamic routes
    for (const pattern of this.sortedPatterns) {
      if (this.matchPath(normalizedPath, pattern)) {
        return this.byPath.get(pattern);
      }
    }

    return undefined;
  }

  /**
   * Match pathname against route pattern
   */
  matchPath(pathname: string, pattern: string): boolean {
    const pathSegments = pathname.split('/').filter(Boolean);
    const patternSegments = pattern.split('/').filter(Boolean);

    // Check for catch-all pattern
    const hasCatchAll = patternSegments.some((s) => s.startsWith('[...'));

    if (!hasCatchAll && pathSegments.length !== patternSegments.length) {
      return false;
    }

    for (let i = 0; i < patternSegments.length; i++) {
      const patternSeg = patternSegments[i];
      const pathSeg = pathSegments[i];

      // Catch-all segment matches everything remaining
      if (patternSeg.startsWith('[...')) {
        return true;
      }

      // Dynamic segment matches any value
      if (patternSeg.startsWith('[') && patternSeg.endsWith(']')) {
        if (!pathSeg) return false; // Must have a value
        continue;
      }

      // Static segment must match exactly
      if (patternSeg !== pathSeg) {
        return false;
      }
    }

    return true;
  }

  /**
   * Extract parameters from pathname based on route pattern
   */
  extractParams(pathname: string, pattern: string): Record<string, string> {
    const params: Record<string, string> = {};
    const pathSegments = pathname.split('/').filter(Boolean);
    const patternSegments = pattern.split('/').filter(Boolean);

    for (let i = 0; i < patternSegments.length; i++) {
      const patternSeg = patternSegments[i];

      // Catch-all segment
      if (patternSeg.startsWith('[...') && patternSeg.endsWith(']')) {
        const paramName = patternSeg.slice(4, -1);
        params[paramName] = pathSegments.slice(i).join('/');
        break;
      }

      // Dynamic segment
      if (patternSeg.startsWith('[') && patternSeg.endsWith(']')) {
        const paramName = patternSeg.slice(1, -1);
        params[paramName] = pathSegments[i];
      }
    }

    return params;
  }

  // ==========================================================================
  // Permission Checks
  // ==========================================================================

  /**
   * Check if user can access a route
   */
  async canAccess(route: RouteConfig, context: AuthContext): Promise<boolean> {
    const { auth, requiredTier, requiredFeatures } = route;

    // Public and optional-auth routes are always accessible.
    if (auth === 'none' || auth === 'optional') {
      return true;
    }

    if (!(await this.canAccessByRole(route, context))) {
      return false;
    }

    // Check billing tier
    if (requiredTier) {
      if (!context.billingTier) return false;
      if (!this.checkTier(requiredTier, context.billingTier)) {
        return false;
      }
    }

    // Check feature flags
    if (requiredFeatures) {
      if (!context.features) return false;
      const hasFeatures = requiredFeatures.every((f) => context.features![f]);
      if (!hasFeatures) return false;
    }

    return true;
  }

  /**
   * WHO may open the route: its auth requirement, requiredRoles (compared as tiers) and
   * permissionCheck. Billing tier and feature flags are not part of it; licensing gates those.
   * This is what the server enforces for every page (lib/routes/access.server.ts, Q-299 P0 #3),
   * because buildAuthContext() knows the caller's roles but not their tier or feature flags.
   */
  async canAccessByRole(route: RouteConfig, context: AuthContext): Promise<boolean> {
    const { auth, requiredRoles, permissionCheck } = route;

    if (auth === 'none' || auth === 'optional') {
      return true;
    }

    if (!context.user) {
      return false;
    }

    if (!this.checkAuthRequirement(auth, context)) {
      return false;
    }

    if (requiredRoles && requiredRoles.length > 0 && !this.checkRoles(requiredRoles, context)) {
      return false;
    }

    if (permissionCheck) {
      return await permissionCheck(context);
    }

    return true;
  }

  /**
   * Check auth requirement against context
   */
  private checkAuthRequirement(
    requirement: RouteConfig['auth'],
    context: AuthContext
  ): boolean {
    if (!context.user) return false;

    switch (requirement) {
      case 'required':
        return true; // Any authenticated user

      case 'staff':
        return this.userHasStaffRole(context);

      case 'admin':
        return this.userHasAdminRole(context);

      case 'platform':
        return this.userHasPlatformRole(context);

      case 'citizen':
        return context.user.role === 'citizen';

      default:
        return true;
    }
  }

  /**
   * Check if user has any of the required roles
   */
  private checkRoles(roles: AppUserRole[], context: AuthContext): boolean {
    if (!context.user) return false;

    const heldSlugs = [
      ...(context.isPlatformAdmin ? ['admin-platform'] : []),
      context.user.role,
      ...(context.user.platformRole ? [context.user.platformRole] : []),
      ...(context.user.roleSlugs || []),
    ];
    // requiredRoles are tier names ('officer' | 'supervisor' | 'admin' ...): a trainee holds
    // the 'officer' tier and an executive the 'admin' tier, exactly as the edge JWT role says.
    const roleSlugs = new Set<string>([
      ...heldSlugs,
      ...heldSlugs.map((slug) => legacyTierForSlug(slug)).filter((tier): tier is string => tier !== null),
    ]);

    // Direct role match
    if (roles.some((role) => roleSlugs.has(role))) {
      return true;
    }

    // Platform admins can access admin-level routes
    if (roleSlugs.has('admin-platform') && roles.includes('admin')) {
      return true;
    }

    return false;
  }

  /**
   * Check billing tier (returns true if user tier >= required tier)
   */
  private checkTier(required: string, actual: string): boolean {
    const tierOrder = ['trial', 'starter', 'professional', 'enterprise'];
    const requiredIndex = tierOrder.indexOf(required);
    const actualIndex = tierOrder.indexOf(actual);
    return actualIndex >= requiredIndex;
  }

  private userHasStaffRole(context: AuthContext): boolean {
    if (!context.user) return false;
    if (context.isPlatformAdmin) return true;
    const roleSlugs = [
      context.user.role,
      ...(context.user.platformRole ? [context.user.platformRole] : []),
      ...(context.user.roleSlugs || []),
    ];
    return roleSlugs.some((role) => STAFF_ROLE_SLUGS.has(role));
  }

  private userHasAdminRole(context: AuthContext): boolean {
    if (!context.user) return false;
    if (context.isPlatformAdmin) return true;
    const roleSlugs = [
      context.user.role,
      ...(context.user.platformRole ? [context.user.platformRole] : []),
      ...(context.user.roleSlugs || []),
    ];
    return roleSlugs.some((role) => ADMIN_ROLE_SLUGS.has(role));
  }

  private userHasPlatformRole(context: AuthContext): boolean {
    if (!context.user) return false;
    return context.isPlatformAdmin === true || context.user.platformRole === 'admin-platform' ||
      (context.user.roleSlugs || []).some((slug) => slug === 'admin-platform' || slug === 'platform-admin');
  }

  /**
   * Get redirect URL for auth failure
   */
  getAuthRedirect(route: RouteConfig, context: AuthContext): string {
    if (route.authRedirect) {
      return typeof route.authRedirect === 'function'
        ? route.authRedirect(context)
        : route.authRedirect;
    }

    if (route.unauthorizedRedirect && context.user) {
      return route.unauthorizedRedirect;
    }

    return AUTH_REDIRECTS[route.auth] || '/login';
  }

  // ==========================================================================
  // Navigation Generation
  // ==========================================================================

  /**
   * Generate full navigation structure for a user
   */
  async generateNavigation(
    context: AuthContext,
    currentPath: string
  ): Promise<GeneratedNavigation> {
    const nav: GeneratedNavigation = {
      main: [],
      sidebar: [],
      mobile: [],
      userMenu: [],
      footer: [],
    };

    // Get all routes with nav config
    const navRoutes = Array.from(this.routes.values()).filter(
      (r) => r.nav && !r.nav.hidden
    );

    // Filter by access and build nav items
    for (const route of navRoutes) {
      const canAccess = await this.canAccess(route, context);
      if (!canAccess) continue;

      const navConfig = route.nav!;
      const item = this.routeToNavItem(route, currentPath, context);

      // Add to each configured position
      for (const position of navConfig.positions) {
        switch (position) {
          case 'main':
            nav.main.push(item);
            break;
          case 'sidebar':
            nav.sidebar = this.addToGroups(nav.sidebar, item);
            break;
          case 'mobile':
            nav.mobile.push(item);
            break;
          case 'user-menu':
            nav.userMenu = this.addToGroups(nav.userMenu, item);
            break;
          case 'footer':
            nav.footer.push(item);
            break;
        }
      }
    }

    // Sort all items
    nav.main = this.sortNavItems(nav.main);
    nav.mobile = this.sortNavItems(nav.mobile);
    nav.footer = this.sortNavItems(nav.footer);
    nav.sidebar = this.sortNavGroups(nav.sidebar);
    nav.userMenu = this.sortNavGroups(nav.userMenu);

    return nav;
  }

  /**
   * Convert route to nav item
   */
  private routeToNavItem(
    route: RouteConfig,
    currentPath: string,
    context: AuthContext
  ): NavItem {
    const navConfig = route.nav!;

    // Resolve badge (may be a function)
    let badge: string | number | null = null;
    if (navConfig.badge) {
      badge =
        typeof navConfig.badge === 'function'
          ? navConfig.badge(context)
          : navConfig.badge;
    }

    return {
      id: route.id,
      path: route.path,
      label: navConfig.label,
      shortLabel: navConfig.shortLabel,
      icon: navConfig.icon,
      group: navConfig.group,
      order: navConfig.order || 0,
      badge,
      external: navConfig.external,
      active: this.isActiveRoute(currentPath, route.path),
    };
  }

  /**
   * Check if route is active (matches current path)
   */
  private isActiveRoute(currentPath: string, routePath: string): boolean {
    // Exact match
    if (currentPath === routePath) return true;

    // Pattern match
    if (this.matchPath(currentPath, routePath)) return true;

    // Check if current path starts with route path (for nested routes)
    if (routePath !== '/' && currentPath.startsWith(routePath + '/')) return true;

    return false;
  }

  /**
   * Add nav item to grouped structure
   */
  private addToGroups(groups: NavGroup[], item: NavItem): NavGroup[] {
    const groupId = item.group || 'default';

    let group = groups.find((g) => g.id === groupId);
    if (!group) {
      group = {
        id: groupId,
        label: NAV_GROUP_LABELS[groupId] || groupId,
        items: [],
        order: NAV_GROUP_ORDER[groupId] ?? 50,
      };
      groups.push(group);
    }

    group.items.push(item);
    return groups;
  }

  /**
   * Sort nav items by order
   */
  private sortNavItems(items: NavItem[]): NavItem[] {
    return [...items].sort((a, b) => a.order - b.order);
  }

  /**
   * Sort nav groups and their items
   */
  private sortNavGroups(groups: NavGroup[]): NavGroup[] {
    return [...groups]
      .map((g) => ({
        ...g,
        items: this.sortNavItems(g.items),
      }))
      .sort((a, b) => a.order - b.order);
  }

  // ==========================================================================
  // Utility Methods
  // ==========================================================================

  /**
   * Get all routes
   */
  getAllRoutes(): RouteConfig[] {
    return Array.from(this.routes.values());
  }

  /**
   * Get routes by portal
   */
  getByPortal(portal: RouteConfig['portal']): RouteConfig[] {
    return Array.from(this.routes.values()).filter((r) => r.portal === portal);
  }

  /**
   * Get routes with navigation config
   */
  getNavigableRoutes(): RouteConfig[] {
    return Array.from(this.routes.values()).filter((r) => r.nav && !r.nav.hidden);
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

/**
 * Global route registry instance
 */
export const routeRegistry = new RouteRegistry();

// ============================================================================
// Convenience Functions
// ============================================================================

/**
 * Get route config by path
 */
export function getRouteByPath(pathname: string): RouteConfig | undefined {
  return routeRegistry.getByPath(pathname);
}

/**
 * Get route config by ID
 */
export function getRouteById(id: string): RouteConfig | undefined {
  return routeRegistry.getById(id);
}

/**
 * Extract params from pathname
 */
export function extractRouteParams(
  pathname: string,
  pattern: string
): Record<string, string> {
  return routeRegistry.extractParams(pathname, pattern);
}

/**
 * Check if user can access route
 */
export async function canAccessRoute(
  route: RouteConfig,
  context: AuthContext
): Promise<boolean> {
  return routeRegistry.canAccess(route, context);
}

/**
 * Generate navigation for user
 */
export async function generateNavigation(
  context: AuthContext,
  currentPath: string
): Promise<GeneratedNavigation> {
  return routeRegistry.generateNavigation(context, currentPath);
}
